<template>
  <div class="two-factor-code" @paste.prevent="onPaste">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      ref="digit"
      :value="digit"
      :disabled="disabled"
      :aria-label="`Dígito ${index + 1} de ${length}`"
      :autocomplete="index === 0 ? 'one-time-code' : 'off'"
      class="two-factor-code__digit"
      type="text"
      inputmode="numeric"
      maxlength="1"
      @input="onInput(index, $event)"
      @keydown="onKeydown(index, $event)"
      @focus="$event.target.select()"/>
  </div>
</template>

<script>
// Captura de un código de 6 dígitos en casillas separadas: avanza sola, retrocede con
// Backspace y acepta pegar el código completo. Emite "input" con el texto y "complete"
// cuando están los 6 dígitos. Mismo comportamiento que la captura de LocalFlow.vue.
export default {
  name: 'TwoFactorCodeInput',
  props: {
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: true }
  },
  data() {
    return {
      length: 6,
      digits: ['', '', '', '', '', '']
    }
  },
  mounted() {
    if (this.autofocus) {
      this.focus(0);
    }
  },
  methods: {
    focus(index = 0) {
      this.$nextTick(() => {
        const input = this.$refs.digit && this.$refs.digit[index];
        if (input) {
          input.focus();
        }
      });
    },
    clear() {
      this.digits = ['', '', '', '', '', ''];
      this.emitChange();
      this.focus(0);
    },
    onInput(index, event) {
      const value = event.target.value.replace(/\D/g, '');
      if (value.length > 1) {
        // Autocompletado del celular o del navegador: llega el código entero en una casilla.
        this.fillFrom(index, value);
        return;
      }
      this.$set(this.digits, index, value);
      event.target.value = value;
      if (value && index < this.length - 1) {
        this.focus(index + 1);
      }
      this.emitChange();
    },
    onKeydown(index, event) {
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        this.$set(this.digits, index - 1, '');
        this.focus(index - 1);
        this.emitChange();
      } else if (event.key === 'ArrowLeft' && index > 0) {
        this.focus(index - 1);
      } else if (event.key === 'ArrowRight' && index < this.length - 1) {
        this.focus(index + 1);
      }
    },
    onPaste(event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      this.fillFrom(0, clipboardData.getData('Text').replace(/\D/g, ''));
    },
    fillFrom(start, numbers) {
      for (let i = 0; i < numbers.length && start + i < this.length; i++) {
        this.$set(this.digits, start + i, numbers[i]);
      }
      const next = this.digits.findIndex(d => !d);
      this.focus(next === -1 ? this.length - 1 : next);
      this.emitChange();
    },
    emitChange() {
      const code = this.digits.join('');
      this.$emit('input', code);
      if (code.length === this.length) {
        this.$emit('complete', code);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_colorsVariables.scss';

.two-factor-code {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.two-factor-code__digit {
  width: 46px;
  height: 54px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  background: #fff;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }

  &:disabled {
    opacity: 0.6;
  }
}

@media (max-width: 400px) {
  .two-factor-code {
    gap: 6px;
  }
  .two-factor-code__digit {
    width: 38px;
    height: 48px;
  }
}
</style>
